import { useCallback, useEffect, useRef, type RefObject } from 'react';
import {
  ERD_IFRAME_URL,
  MSG_COPY_TO_CLIPBOARD,
  MSG_FUSION_USAGE_DATA,
  MSG_OPEN_SCENARIO,
  MSG_READY,
  MSG_REQUEST_WORKSPACE_METADATA,
  MSG_REQUEST_WORKSPACES,
  MSG_SET_THEME,
  MSG_WORKSPACE_METADATA,
  MSG_WORKSPACES_DATA,
  SOURCE_INBOUND,
  SOURCE_OUTBOUND,
  type PlanningWorkspaceEnriched,
} from './erd-bridge.js';
import { ErdGateway } from './erd-gateway.js';
import type { ApiClient } from '../../lib/api-client.js';

export interface OpenScenarioPayload {
  fusionScenarioId: number;
  scenarioName: string;
  fusionTeamId?: number | null;
}

type Send = (type: string, data: unknown) => void;
type InboundHandler = (send: Send, data: unknown) => void | Promise<void>;

interface UseErdIframeBridgeOptions {
  /** Customer API client (from useAuth). */
  api: ApiClient;
  /** `'primary'` (default) or `'secondary:<n>'` for secondary connections. */
  workspaceRef?: string;
  /** Fired when the iframe sends a scenario-click message — parent opens
   *  a preview dialog. Optional; bridge stays usable without it. */
  onOpenScenario?: (payload: OpenScenarioPayload) => void;
}

/**
 * postMessage bridge between the parent SPA and the external ER-Diagram
 * iframe. Mirrors `wf-audit/src/slices/planning/hooks/use-erd-iframe-bridge.ts`
 * verbatim with three differences:
 *   - Uses TanStack-friendly `ApiClient` (from `useAuth`) instead of the
 *     legacy `fetch('/api/...')` calls — `ErdGateway` wraps both.
 *   - Theme is hard-coded to `dark` (the new SPA is dark-only; legacy
 *     had `next-themes`).
 *   - Workspace ref defaults to `'primary'` and is the only non-license
 *     argument — secondary workspace picker lives on the page above.
 *
 * The hook returns `{onIframeLoad}` — bind to the iframe's `onLoad`. The
 * iframe sends `READY` once it's mounted, and the parent kicks off the
 * theme handshake. After that the iframe drives the data flow with
 * `REQUEST_*` messages.
 */
export function useErdIframeBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  options: UseErdIframeBridgeOptions,
): { onIframeLoad: () => void } {
  const { api, workspaceRef = 'primary', onOpenScenario } = options;

  // Stable ref to onOpenScenario so re-renders don't tear down the
  // listener. Without this, switching React strict-mode tabs would
  // double-bind the postMessage listener.
  const onOpenScenarioRef = useRef(onOpenScenario);
  onOpenScenarioRef.current = onOpenScenario;

  const sendTheme = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      { source: SOURCE_OUTBOUND, type: MSG_SET_THEME, data: { theme: 'dark' } },
      ERD_IFRAME_URL,
    );
  }, [iframeRef]);

  useEffect(() => {
    const gateway = new ErdGateway(api, workspaceRef);

    const send: Send = (type, data) => {
      iframeRef.current?.contentWindow?.postMessage(
        { source: SOURCE_OUTBOUND, type, data },
        ERD_IFRAME_URL,
      );
    };

    const handlers: Record<string, InboundHandler> = {
      [MSG_READY]: () => {
        sendTheme();
      },

      [MSG_REQUEST_WORKSPACES]: async (send) => {
        try {
          const workspaces = await gateway.getWorkspaces();
          const creatorIds = Array.from(
            new Set(workspaces.map((w) => w.createdBy).filter(Boolean)),
          );
          const userMap = creatorIds.length > 0 ? await gateway.getUsersByIds(creatorIds) : {};
          const enriched: PlanningWorkspaceEnriched[] = workspaces.map((w) => ({
            ...w,
            createdByName: (w.createdBy && userMap[w.createdBy]) || null,
          }));
          send(MSG_WORKSPACES_DATA, enriched);
        } catch (err) {
          console.error('[ERDiagram] Failed to fetch workspaces:', err);
          // Send an empty payload so the iframe doesn't sit on its loading
          // spinner forever. Operator sees the network error in their dev
          // tools; future improvement is a parent-side toast.
          send(MSG_WORKSPACES_DATA, []);
        }
      },

      [MSG_REQUEST_WORKSPACE_METADATA]: async (send, data) => {
        const workspaceId: string | undefined = (data as { workspaceId?: string } | undefined)
          ?.workspaceId;
        if (!workspaceId) return;
        try {
          send(MSG_WORKSPACE_METADATA, await gateway.getWorkspaceMetadata(workspaceId));
        } catch (err) {
          console.error('[ERDiagram] Failed to fetch workspace metadata:', err);
        }
        // Fire-and-forget: fetch Fusion usage after metadata so the diagram
        // renders first. On failure, we emit an empty array so the iframe
        // exits its loading state.
        gateway
          .getFusionUsage(workspaceId)
          .then((items) => send(MSG_FUSION_USAGE_DATA, items))
          .catch((err) => {
            console.error('[ERDiagram] Failed to fetch fusion usage:', err);
            send(MSG_FUSION_USAGE_DATA, []);
          });
      },

      [MSG_OPEN_SCENARIO]: (_send, data) => {
        const payload = data as OpenScenarioPayload | undefined;
        const callback = onOpenScenarioRef.current;
        if (!payload?.fusionScenarioId || !callback) return;
        callback(payload);
      },

      [MSG_COPY_TO_CLIPBOARD]: (_send, data) => {
        const text = (data as { text?: string } | undefined)?.text;
        if (text && navigator.clipboard?.writeText) {
          void navigator.clipboard.writeText(text);
        }
      },
    };

    function onMessage(event: MessageEvent): void {
      // Origin check — only honour messages from the iframe's origin.
      if (event.origin !== ERD_IFRAME_URL) return;
      const data = event.data as { source?: string; type?: string; data?: unknown } | null;
      if (!data || data.source !== SOURCE_INBOUND || typeof data.type !== 'string') return;
      const handler = handlers[data.type];
      if (handler) void handler(send, data.data);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [api, workspaceRef, sendTheme, iframeRef]);

  return { onIframeLoad: sendTheme };
}
